import { Layout, theme } from "antd";
import React, { useMemo, useState } from "react";
import { Route, Routes, Link } from "react-router-dom";

import Error404 from "../pages/errors/Error404";
import MobileMenuDrawer from "./MobileMenuDrawer";
import Top from "./Top";
import routes, { AppRoute } from "./routes";
import SidebarMenu from "./SidebarMenu";
import { SIDEBAR_WIDTH_EXPANDED } from "./layoutConstants";

import AdminProfile from "../pages/Profile";
import RecipientsMainComp from "../pages/recipients/RecipientsMainComp";
import ManageUsersMainComp from "../pages/users/ManageUsersMainComp";
import SubAdminMainComp from "../pages/subAdmin/SubAdminMainComp";
import MainServiceComp from "../pages/website/services/MainServiceComp";
import MainTeamComp from "../pages/website/team/MainTeamComp";
import BlogsMainComp from "../pages/blogs/BlogsMainComp";
import ReportsLibraryMainComp from "../pages/reports/ReportsLibraryMainComp";

import { useUserStore } from "../../store/slices/user";
import { IUserInfo } from "@src/types/api/user";
import ChatWidget from "../../components/AiAgent/ChatWidget";
import { TrialBanner } from "../../components/TrialBanner";
import { useGetMyCompany } from "../../hooks/api/company/hooks";



export function hasPermission(
  user: IUserInfo | null | undefined,
  module?: string,
  action?: string
) {
  if (!user) return false;

  // Route does not require permission
  if (!module || !action) {
    return true;
  }

  return user.permissions?.some(
    (p) =>
      p.module === module &&
      p.action === action
  );
}


const ProtectedRoute = ({
  component: Component,
  permission,
}: any) => {
  const { user } = useUserStore();

  if (
    !hasPermission(
      user,
      permission?.module,
      permission?.action
    )
  ) {
    return <Error404 />;
  }

  return <Component />;
};


const MyLayout: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const { user } = useUserStore();
  const { data: companyRes } = useGetMyCompany();
  const company = companyRes?.data as any;

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const showDrawer = () => {
    setOpen(true);
  };

  // ✅ Sidebar routes (KEEP children for dropdown)
const allowedRoutes = useMemo(() => {
  if (!user) return [];

  return routes.filter((route) =>
    hasPermission(
      user,
      route.permission?.module,
      route.permission?.action
    )
  );
}, [user]);

  // ✅ Flatten + Filter routes for React Router
const allowedFlatRoutes = useMemo(() => {
  if (!user) return [];

  const temp: AppRoute[] = [];

  function flatten(
    arr: AppRoute[],
    baseUrl: string = ""
  ) {
    arr.forEach((route) => {
      const fullUrl = `${baseUrl}${route.url}`;

      const allowed = hasPermission(
        user,
        route.permission?.module,
        route.permission?.action
      );

      if (!allowed) return;

      if (route.children) {
        flatten(route.children, fullUrl);
      } else {
        temp.push({
          ...route,
          url: fullUrl.replace(/\/+/g, "/"),
        });
      }
    });
  }

  flatten(routes);

  return temp;
}, [user]);

  return (
    <Layout className="h-screen overflow-hidden bg-slate-50">

      {/* Sidebar */}
      <Layout.Sider
        collapsible
        width={SIDEBAR_WIDTH_EXPANDED}
        collapsed={collapsed}
        trigger={null}
        className="sm:block hidden overflow-y-auto h-screen"
      >

        <SidebarMenu collapsed={collapsed} routes={allowedRoutes} />
      </Layout.Sider>

      {/* Mobile Drawer */}
      <MobileMenuDrawer open={open} setOpen={setOpen} routes={allowedRoutes} />

      {/* Main Layout */}
      <Layout className="bg-slate-50 h-screen overflow-hidden flex flex-col">
        <Top {...{ collapsed, setCollapsed, showDrawer }} />

        <Layout.Content className="p-3 sm:p-5 text-black overflow-auto flex-1 min-h-0">
          <TrialBanner
            trialEndsAt={company?.trial_ends_at}
            planType={company?.plan_type}
            cancelledAt={company?.cancelled_at}
          />
          <ChatWidget />
          <Routes>

            {allowedFlatRoutes.map((v) =>
              v.component && v.url ? (
                <Route
                  path={v.url}
                  key={v.url}
                  element={
                    <ProtectedRoute
                      component={v.component}
                      permission={v.permission}
                    />
                  }
                />
              ) : null
            )}

            {/* ✅ Static Routes */}
            <Route path="*" element={<Error404 />} />
            <Route path="/profile" element={<AdminProfile />} />

            <Route path="/recipients/create" element={<RecipientsMainComp />} />
            <Route path="/recipients/edit/:id" element={<RecipientsMainComp />} />

            <Route path="/users/create" element={<ManageUsersMainComp />} />
            <Route path="/users/edit/:id" element={<ManageUsersMainComp />} />

            <Route path="/sub-admin/create" element={<SubAdminMainComp />} />
            <Route path="/sub-admin/edit/:id" element={<SubAdminMainComp />} />

            {/* Website CRUD */}
            <Route path="/website/services/create" element={<MainServiceComp />} />
            <Route path="/website/services/edit/:id" element={<MainServiceComp />} />

            <Route path="/website/team/create" element={<MainTeamComp />} />
            <Route path="/website/team/edit/:id" element={<MainTeamComp />} />

            <Route path="/website/blog/create" element={<BlogsMainComp />} />
            <Route path="/website/blog/edit/:id" element={<BlogsMainComp />} />

            {/* Reports */}
            <Route path="/reports/:id" element={<ReportsLibraryMainComp />} />

          </Routes>
        </Layout.Content>
      </Layout>
    </Layout>
  );
};

export default MyLayout;